import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi, alertApiRef } from '@backstage/core-plugin-api';
import { Header, Page, Content } from '@backstage/core-components';
import { makeStyles } from 'tss-react/mui';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DeleteIcon from '@mui/icons-material/Delete';
import HomeIcon from '@mui/icons-material/Home';
import EditIcon from '@mui/icons-material/Edit';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import { PlayCircleOutline as PlayCircleOutlineIcon, Warning as WarningIcon } from '@mui/icons-material';
import { oneadvancedApiRef } from '../../../../api/OneAdvancedApi';
import type {
  Banner,
  BannerSeverity,
  BannerStatus,
  CreateBannerInput,
  UpdateBannerInput,
} from '../../types';
import { BannerForm } from './BannerForm';

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const useStyles = makeStyles()((theme) => ({
  breadcrumbs: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap' as const,
    gap: 4,
    marginBottom: theme.spacing(2.5),
    fontSize: 13,
    color: theme.palette.text.secondary,
  },
  bcLink: {
    cursor: 'pointer',
    color: theme.palette.primary.main,
    '&:hover': { textDecoration: 'underline' },
    display: 'flex',
    alignItems: 'center',
    gap: 4,
  },
  bcCurrent: {
    color: theme.palette.text.primary,
    fontWeight: 600,
  },
  bcSep: {
    display: 'flex',
    alignItems: 'center',
    color: theme.palette.text.secondary,
  },
  toolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  filters: {
    display: 'flex',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
  },
  limitWarning: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 2),
    marginBottom: theme.spacing(2),
    backgroundColor: '#FFF4E5',
    border: '1px solid #FF9800',
    borderRadius: 4,
    color: '#663C00',
    fontSize: 14,
  },
  emptyCell: {
    textAlign: 'center',
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
  actions: {
    display: 'flex',
    gap: 2,
    justifyContent: 'flex-end',
  },
}));

// ---------------------------------------------------------------------------
// Severity chip config
// ---------------------------------------------------------------------------

const SEVERITY_COLOURS: Record<BannerSeverity, { bg: string; label: string }> = {
  high: { bg: '#F44336', label: 'High' },
  medium: { bg: '#FF9800', label: 'Medium' },
  low: { bg: '#4CAF50', label: 'Low' },
  info: { bg: '#03A9F4', label: 'Info' },
};

const STATUS_COLOURS: Record<BannerStatus, { color: 'default' | 'primary' | 'secondary'; label: string }> = {
  enabled: { color: 'primary', label: 'Enabled' },
  disabled: { color: 'default', label: 'Disabled' },
  expired: { color: 'secondary', label: 'Expired' },
};

type FilterType = 'all' | BannerStatus;

const FILTERS: { key: FilterType; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'enabled', label: 'Active' },
  { key: 'disabled', label: 'Disabled' },
  { key: 'expired', label: 'Expired' },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AdminBannersPage() {
  const { classes } = useStyles();
  const api = useApi(oneadvancedApiRef);
  const alertApi = useApi(alertApiRef);
  const navigate = useNavigate();

  const [banners, setBanners] = useState<Banner[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<FilterType>('all');
  const [formOpen, setFormOpen] = useState(false);
  const [editingBanner, setEditingBanner] = useState<Banner | undefined>(undefined);
  const [deleteTarget, setDeleteTarget] = useState<Banner | undefined>(undefined);

  const activeCount = banners.filter(b => b.status === 'enabled').length;

  const fetchBanners = useCallback(async () => {
    try {
      const data = await api.banners.getAllBanners();
      setBanners(data);
    } catch (e: any) {
      alertApi.post({ message: `Failed to load banners: ${e.message}`, severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [api, alertApi]);

  useEffect(() => {
    fetchBanners();
  }, [fetchBanners]);

  const visible = filter === 'all' ? banners : banners.filter(b => b.status === filter);

  // ── Form save ────────────────────────────────────────────────────────────

  const handleSave = async (data: CreateBannerInput | UpdateBannerInput) => {
    try {
      if (editingBanner) {
        await api.banners.updateBanner(editingBanner.id, data as UpdateBannerInput);
        alertApi.post({ message: 'Banner updated.', severity: 'success' });
      } else {
        await api.banners.createBanner(data as CreateBannerInput);
        alertApi.post({ message: 'Banner created.', severity: 'success' });
      }
      await fetchBanners();
    } catch (e: any) {
      alertApi.post({ message: e.message, severity: 'error' });
      throw e; // re-throw so BannerForm keeps dialog open
    }
  };

  // ── Enable / Disable ─────────────────────────────────────────────────────

  const handleToggle = async (banner: Banner) => {
    try {
      if (banner.status === 'enabled') {
        await api.banners.disableBanner(banner.id);
        alertApi.post({ message: 'Banner disabled.', severity: 'success' });
      } else {
        await api.banners.enableBanner(banner.id);
        alertApi.post({ message: 'Banner enabled.', severity: 'success' });
      }
      await fetchBanners();
    } catch (e: any) {
      alertApi.post({ message: e.message, severity: 'error' });
    }
  };

  // ── Delete ───────────────────────────────────────────────────────────────

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      await api.banners.deleteBanner(deleteTarget.id);
      alertApi.post({ message: 'Banner deleted.', severity: 'success' });
      setDeleteTarget(undefined);
      await fetchBanners();
    } catch (e: any) {
      alertApi.post({ message: e.message, severity: 'error' });
    }
  };

  // ── Row actions ──────────────────────────────────────────────────────────

  function renderActions(banner: Banner) {
    const atLimit = activeCount >= 3 && banner.status !== 'enabled';
    let toggleTooltip: string;
    if (banner.status === 'enabled') {
      toggleTooltip = 'Disable';
    } else if (atLimit) {
      toggleTooltip = 'Maximum 3 active banners reached';
    } else {
      toggleTooltip = 'Enable';
    }

    return (
      <div className={classes.actions}>
        <Tooltip title="Edit">
          <IconButton
            size="small"
            onClick={() => { setEditingBanner(banner); setFormOpen(true); }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title={toggleTooltip}>
          <span>
            <IconButton
              size="small"
              onClick={() => handleToggle(banner)}
              disabled={atLimit && banner.status !== 'enabled'}
            >
              {banner.status === 'enabled' ? (
                <PauseCircleOutlineIcon fontSize="small" />
              ) : (
                <PlayCircleOutlineIcon fontSize="small" />
              )}
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title="Delete">
          <IconButton size="small" onClick={() => setDeleteTarget(banner)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </div>
    );
  }

  function renderTableRows() {
    if (loading) {
      return (
        <TableRow>
          <TableCell colSpan={6} className={classes.emptyCell}>
            <CircularProgress size={24} />
          </TableCell>
        </TableRow>
      );
    }
    if (visible.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={6} className={classes.emptyCell}>
            <Typography variant="body2">No banners found.</Typography>
          </TableCell>
        </TableRow>
      );
    }
    return visible.map(banner => renderBannerRow(banner));
  }

  function renderBannerRow(banner: Banner) {
    return (
      <TableRow key={banner.id} hover>
        <TableCell>
          <strong>{banner.title}</strong>
          <br />
          <Typography variant="caption" color="textSecondary">
            {banner.message.length > 80
              ? `${banner.message.slice(0, 80)}…`
              : banner.message}
          </Typography>
        </TableCell>
        <TableCell>
          <Chip
            size="small"
            label={SEVERITY_COLOURS[banner.severity]?.label ?? banner.severity}
            style={{
              backgroundColor: SEVERITY_COLOURS[banner.severity]?.bg,
              color: '#fff',
              fontWeight: 600,
            }}
          />
        </TableCell>
        <TableCell>
          <Chip
            size="small"
            label={STATUS_COLOURS[banner.status]?.label ?? banner.status}
            color={STATUS_COLOURS[banner.status]?.color ?? 'default'}
          />
        </TableCell>
        <TableCell>
          {banner.expiresAt
            ? new Date(banner.expiresAt).toLocaleString()
            : '—'}
        </TableCell>
        <TableCell>{banner.dismissable ? 'Yes' : 'No'}</TableCell>
        <TableCell align="right">{renderActions(banner)}</TableCell>
      </TableRow>
    );
  }

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <Page themeId="tool">
      <Header
        title="Banner Management"
        subtitle="Manage site-wide announcement banners"
      />
      <Content>
        {/* Breadcrumbs */}
        <div className={classes.breadcrumbs}>
          <span
            className={classes.bcLink}
            onClick={() => navigate('/oneadvanced-admin')}
            role="button"
            tabIndex={0}
            onKeyDown={e => e.key === 'Enter' && navigate('/oneadvanced-admin')}
          >
            <HomeIcon style={{ fontSize: 14 }} />
            Admin Hub
          </span>
          <span className={classes.bcSep}>
            <ChevronRightIcon style={{ fontSize: 16 }} />
          </span>
          <span className={classes.bcCurrent}>Banners</span>
        </div>

        {/* Active limit warning */}
        {activeCount >= 3 && (
          <div className={classes.limitWarning}>
            <WarningIcon fontSize="small" />
            <span>3 of 3 banners are currently active. Disable one to enable another.</span>
          </div>
        )}

        {/* Toolbar */}
        <div className={classes.toolbar}>
          <div className={classes.filters}>
            {FILTERS.map(f => (
              <Chip
                key={f.key}
                label={f.label}
                onClick={() => setFilter(f.key)}
                color={filter === f.key ? 'primary' : 'default'}
                clickable
                variant={filter === f.key ? 'filled' : 'outlined'}
              />
            ))}
          </div>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => { setEditingBanner(undefined); setFormOpen(true); }}
          >
            Add Banner
          </Button>
        </div>

        {/* Table */}
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Title</TableCell>
                <TableCell>Severity</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Expires</TableCell>
                <TableCell>Dismissable</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {renderTableRows()}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Create / Edit dialog */}
        <BannerForm
          open={formOpen}
          onClose={() => setFormOpen(false)}
          onSave={handleSave}
          banner={editingBanner}
        />

        {/* Delete confirmation */}
        <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(undefined)}>
          <DialogTitle>Delete Banner</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to permanently delete{' '}
              <strong>{deleteTarget?.title}</strong>? This cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteTarget(undefined)}>Cancel</Button>
            <Button onClick={handleDeleteConfirm} color="secondary" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Content>
    </Page>
  );
}
